import {
  RemoteNotFoundError,
  type CreateResult,
  type GistBackendConfig,
  type RemoteBackend,
  type RemoteSnapshot,
  type UpdateResult,
} from './types';

const GIST_DESCRIPTION = 'knittime knitting state backup';
const GITHUB_API = 'https://api.github.com';

interface GistApiResponse {
  id: string;
  updated_at: string;
  files: Record<string, { content: string } | undefined>;
}

function githubHeaders(pat: string, extra?: Record<string, string>): Record<string, string> {
  return {
    Authorization: `token ${pat}`,
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'Content-Type': 'application/json',
    ...extra,
  };
}

async function throwOnError(res: Response): Promise<void> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    if (res.status === 404) {
      throw new RemoteNotFoundError(body.message ?? res.statusText);
    }
    throw new Error(`GitHub API ${res.status}: ${body.message ?? res.statusText}`);
  }
}

function toFiles(payload: Record<string, string>): Record<string, { content: string }> {
  const out: Record<string, { content: string }> = {};
  for (const [name, content] of Object.entries(payload)) {
    out[name] = { content };
  }
  return out;
}

export class GistBackend implements RemoteBackend {
  readonly kind = 'gist' as const;
  private readonly pat: string;

  constructor(pat: string) {
    this.pat = pat;
  }

  async create(files: Record<string, string>): Promise<CreateResult> {
    const res = await fetch(`${GITHUB_API}/gists`, {
      method: 'POST',
      headers: githubHeaders(this.pat),
      body: JSON.stringify({
        description: GIST_DESCRIPTION,
        public: false,
        files: toFiles(files),
      }),
    });
    await throwOnError(res);
    const data = (await res.json()) as GistApiResponse;
    return {
      remoteId: data.id,
      cursor: res.headers.get('etag') ?? '',
      updatedAt: data.updated_at,
    };
  }

  async fetch(remoteId: string, prevCursor?: string): Promise<RemoteSnapshot | null> {
    const extra = prevCursor ? { 'If-None-Match': prevCursor } : undefined;
    const res = await fetch(`${GITHUB_API}/gists/${remoteId}`, {
      headers: githubHeaders(this.pat, extra),
    });
    if (res.status === 304) return null;
    await throwOnError(res);
    const data = (await res.json()) as GistApiResponse;
    const files: Record<string, string | undefined> = {};
    for (const [name, file] of Object.entries(data.files)) {
      files[name] = file?.content;
    }
    return {
      files,
      cursor: res.headers.get('etag') ?? '',
      updatedAt: data.updated_at,
    };
  }

  async update(remoteId: string, files: Record<string, string>): Promise<UpdateResult> {
    const res = await fetch(`${GITHUB_API}/gists/${remoteId}`, {
      method: 'PATCH',
      headers: githubHeaders(this.pat),
      body: JSON.stringify({ files: toFiles(files) }),
    });
    await throwOnError(res);
    const data = (await res.json()) as GistApiResponse;
    return {
      cursor: res.headers.get('etag') ?? '',
      updatedAt: data.updated_at,
    };
  }
}

export function makeGistBackend(config: GistBackendConfig): GistBackend {
  return new GistBackend(config.pat);
}
